import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import styles from './index.module.scss';
import closeIcon from '../../../../../assets/images/close.png';
import checkmarkIcon from '../../../../../assets/images/checkmark.png';
import FolderView from '../FolderView';

interface TabItem {
  title: string;
  permission?: string;
  order: number;
}

interface FolderItem {
  _id: string;
  title: string;
  description?: string;
  status?: string;
  permissions?: string | string[];
  contentItems?: any[];
  customTabs?: Array<{
    tabTitle: string;
    tabOrder: number;
    templateTitle: string;
    templateId: string;
    tabSelected: boolean;
  }>;
}

interface FolderModalProps {
  isOpen: boolean;
  toggle: () => void;
  onConnectFolder: (folderId: string, selectedTabTitle: string) => void;
  templateTitle: string;
  tabItems: TabItem[];
  foldersList: FolderItem[];
}

const FolderModal: React.FC<FolderModalProps> = ({
  isOpen,
  toggle,
  onConnectFolder,
  templateTitle,
  tabItems,
  foldersList
}) => {
  const [selectedTab, setSelectedTab] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredFolders, setFilteredFolders] = useState<FolderItem[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  // Filter folders based on search term and selected tab
  useEffect(() => {
    let filtered = foldersList;

    // Filter by selected tab - show only folders that are NOT already connected to this tab
    if (selectedTab) {
      filtered = filtered.filter(folder => {
        if (!folder.customTabs || folder.customTabs.length === 0) {
          return true; // Show folders with no customTabs
        }
        
        // Check if this folder is already connected to the selected tab
        const isConnectedToSelectedTab = folder.customTabs.some(tab => 
          tab.tabTitle === selectedTab && tab.tabSelected
        );
        
        return !isConnectedToSelectedTab; // Show only folders NOT connected to selected tab
      });
    }

    // Apply search filter
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(folder =>
        folder.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredFolders(filtered);
  }, [searchTerm, foldersList, selectedTab]);

  // Set first tab as selected by default
  useEffect(() => {
    if (tabItems && tabItems.length > 0 && !selectedTab) {
      const firstTab = tabItems.sort((a, b) => a.order - b.order)[0];
      setSelectedTab(firstTab.title);
    }
  }, [tabItems, selectedTab]);

  const handleToggleExpansion = (folderId: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const handleFolderSelect = (folderId: string) => {
    setSelectedFolder(prev => prev === folderId ? '' : folderId);
  };

  const handleConnect = () => {
    if (selectedFolder && selectedTab) {
      onConnectFolder(selectedFolder, selectedTab);
    }
  };

  const handleCancel = () => {
    setSelectedTab('');
    setSearchTerm('');
    setSelectedFolder('');
    setExpandedFolders(new Set());
    toggle();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleCancel}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Connect Folders</h2>
          <p className={styles.modalSubtitle}>Related to <b>{templateTitle}</b></p>
          <button className={styles.closeButton} onClick={handleCancel}>
            <img src={closeIcon} alt="Close" className={styles.closeIcon} />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          {/* Available Tabs Section */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Available Tabs</h3>
            <div className={styles.tabsContainer}>
              {tabItems && tabItems.length > 0 ? (
                tabItems
                  .sort((a, b) => a.order - b.order)
                  .map((tab) => (
                    <div
                      key={tab.title}
                      className={`${styles.tabPill} ${
                        selectedTab === tab.title ? styles.selectedTab : ''
                      }`}
                      onClick={() => setSelectedTab(tab.title)}
                    >
                      <span>{tab.title}</span>
                      {selectedTab === tab.title && (
                        <div className={styles.checkmark}>
                          <img src={checkmarkIcon} alt="Selected" className={styles.checkmarkIcon} />
                        </div>
                      )}
                    </div>
                  ))
              ) : (
                <p className={styles.noTabsMessage}>No tabs available</p>
              )}
            </div>
          </div>

          {/* Folders Section */}
          <div className={styles.section} style={{ backgroundColor: 'transparent', padding: 0 }}>
            <h3 className={styles.sectionTitle} style={{ marginBottom: 10 }}>Folders</h3>
            <div className={styles.searchContainer}>
              <div className={styles.searchInputWrapper}>
                <Search className={styles.searchIcon} size={18} color="#000" />
                <input
                  type="text"
                  placeholder="Search by folder Name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
            </div>
            
            <div className={styles.foldersList}>
              {searchTerm && (
                <div className={styles.searchResultsCount}>
                  {filteredFolders.length} result{filteredFolders.length !== 1 ? 's' : ''}
                </div>
              )}
              {filteredFolders.length > 0 ? (
                <div className={styles.folderViewContainer}>
                  <FolderView
                    folders={filteredFolders}
                    expandedFolders={expandedFolders}
                    onToggleExpansion={handleToggleExpansion}
                    onContentItemPreview={() => {}} // Not needed in modal
                    onDragEnd={() => {}} // Not needed in modal
                    onRemoveFolder={undefined} // Not needed in modal
                    onAssignFolder={undefined} // Not needed in modal
                    showDragHandle={false}
                    showAssignButton={false} // Not needed in modal
                    className={styles.folderView}
                    openMarginLeft={28}
                    // Selection props
                    selectedFolder={selectedFolder}
                    onFolderSelect={handleFolderSelect}
                    showSelection={true}
                  />
                </div>
              ) : (
                <p className={styles.noFoldersMessage}>
                  {searchTerm ? 'No folders found matching your search' : 'No folders available'}
                </p>
              )}
            </div>
          </div>
        </div>
        
        <div className={styles.modalFooter}>
          <button 
            onClick={handleConnect} 
            className={styles.connectButton}
            disabled={!selectedFolder || !selectedTab}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

export default FolderModal;
